import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '@app/components/Drawer';
import { SaveIcon } from '@app/components/icons';
import CopyField from '@app/components/CopyField';
import { useIdpConnectionsApi, useSecretsApi } from '@app/hooks/useApis';
import {
  defaultOidcConfig,
  defaultSamlConfig,
  parseSamlMetadata,
  type ParsedSamlMetadata,
  isOidcConnection,
  isSamlConnection
} from '@app/utils/idp';
import { readFileAsText } from '@app/utils/files';
import type {
  IdpConnection,
  IdpConnectionCreate,
  IdpConnectionOidcCreate,
  IdpConnectionOidcCreateConfig,
  IdpConnectionSamlCreate,
  IdpConnectionSamlCreateConfig
} from '@app/types/sdk';
import { ResponseError } from '@app/types/sdk';
import { deleteSecretWithAuth } from '@app/utils/secrets';

export interface ConnectionWizardResult {
  connection: IdpConnection;
  etag?: string | null;
  openDrawer: boolean;
}

export interface ConnectionWizardProps {
  orgId: string;
  open: boolean;
  preferredType?: 'oidc' | 'saml';
  onClose: () => void;
  onCreated: (result: ConnectionWizardResult) => void;
}

type SamlInputMode = 'metadataFile' | 'metadataUrl' | 'manual';

const statusOptions: Array<{ value: 'active' | 'disabled'; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' }
];

type TokenAuthMethod = NonNullable<IdpConnectionOidcCreateConfig['tokenAuthMethod']>;
const tokenAuthOptions: Array<{ value: TokenAuthMethod; label: string }> = [
  { value: 'client_secret_basic', label: 'client_secret_basic' },
  { value: 'client_secret_post', label: 'client_secret_post' }
];

type SamlNameIdFormat = NonNullable<IdpConnectionSamlCreateConfig['nameIdFormat']>;
const nameIdOptions: Array<{ value: SamlNameIdFormat; label: string }> = [
  { value: 'unspecified', label: 'Unspecified' },
  { value: 'emailAddress', label: 'EmailAddress' },
  { value: 'persistent', label: 'Persistent' }
];

const ConnectionWizard = ({ orgId, open, preferredType, onClose, onCreated }: ConnectionWizardProps) => {
  const idpConnectionsApi = useIdpConnectionsApi();
  const secretsApi = useSecretsApi();

  const [connectionType, setConnectionType] = useState<'oidc' | 'saml'>(preferredType ?? 'oidc');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  const [description, setDescription] = useState('');

  const [oidcConfig, setOidcConfig] = useState<IdpConnectionOidcCreateConfig>(defaultOidcConfig());
  const [oidcScopesInput, setOidcScopesInput] = useState('openid email profile');
  const [oidcDiscovering, setOidcDiscovering] = useState(false);
  const [oidcDiscoveryMessage, setOidcDiscoveryMessage] = useState<string | null>(null);
  const [clientSecretValue, setClientSecretValue] = useState('');

  const [samlConfig, setSamlConfig] = useState<IdpConnectionSamlCreateConfig>(defaultSamlConfig());
  const [samlInputMode, setSamlInputMode] = useState<SamlInputMode>('metadataFile');
  const [samlMetadataUrl, setSamlMetadataUrl] = useState('');
  const [samlMetadataPreview, setSamlMetadataPreview] = useState<ParsedSamlMetadata | null>(null);
  const [samlMetadataFileName, setSamlMetadataFileName] = useState<string | null>(null);
  const [samlCertificateInput, setSamlCertificateInput] = useState('');

  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [createdResult, setCreatedResult] = useState<ConnectionWizardResult | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const resetState = useCallback(
    (nextType?: 'oidc' | 'saml') => {
      setConnectionType(nextType ?? preferredType ?? 'oidc');
      setName('');
      setStatus('active');
      setDescription('');
      setOidcConfig(defaultOidcConfig());
      setOidcScopesInput('openid email profile');
      setOidcDiscoveryMessage(null);
      setClientSecretValue('');
      setSamlConfig(defaultSamlConfig());
      setSamlInputMode('metadataFile');
      setSamlMetadataUrl('');
      setSamlMetadataPreview(null);
      setSamlMetadataFileName(null);
      setSamlCertificateInput('');
      setFormError(null);
      setSubmitting(false);
      setCreatedResult(null);
      setShowSuccess(false);
    },
    [preferredType]
  );

  useEffect(() => {
    if (open) {
      resetState(preferredType);
    }
  }, [open, preferredType, resetState]);

  const scopes = useMemo(() => {
    return oidcScopesInput
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  }, [oidcScopesInput]);

  const handleOidcConfigChange = <K extends keyof IdpConnectionOidcCreateConfig>(key: K, value: IdpConnectionOidcCreateConfig[K]) => {
    setOidcConfig((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSamlConfigChange = <K extends keyof IdpConnectionSamlCreateConfig>(key: K, value: IdpConnectionSamlCreateConfig[K]) => {
    setSamlConfig((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const validateBasics = () => {
    if (name.trim().length === 0) {
      setFormError('Connection name is required.');
      return false;
    }
    setFormError(null);
    return true;
  };

  const validateOidc = () => {
    if (oidcConfig.issuer.trim().length === 0) {
      setFormError('Issuer URL is required.');
      return false;
    }
    if (oidcConfig.clientId.trim().length === 0) {
      setFormError('Client ID is required.');
      return false;
    }
    if (clientSecretValue.trim().length === 0) {
      setFormError('Client secret is required.');
      return false;
    }
    setFormError(null);
    return true;
  };

  const validateSaml = () => {
    if (samlInputMode === 'metadataUrl') {
      if (samlMetadataUrl.trim().length === 0) {
        setFormError('Enter a metadata URL.');
        return false;
      }
    }

    if (samlInputMode === 'manual' || samlInputMode === 'metadataFile') {
      if ((samlConfig.idpEntityId ?? '').trim().length === 0) {
        setFormError('IdP Entity ID is required.');
        return false;
      }
      if ((samlConfig.idpSsoUrl ?? '').trim().length === 0) {
        setFormError('IdP SSO URL is required.');
        return false;
      }
      if ((samlConfig.idpCertificate ?? '').trim().length === 0 && samlCertificateInput.trim().length === 0) {
        setFormError('Provide at least one IdP signing certificate (PEM).');
        return false;
      }
    }

    setFormError(null);
    return true;
  };

  const handleOidcDiscover = async () => {
    if (oidcConfig.issuer.trim().length === 0) {
      setFormError('Enter an issuer URL before running discovery.');
      return;
    }

    try {
      setOidcDiscovering(true);
      setOidcDiscoveryMessage(null);
      const response = await fetch(`${oidcConfig.issuer.replace(/\/+$/, '')}/.well-known/openid-configuration`, {
        method: 'GET'
      });
      if (!response.ok) {
        throw new Error(`Discovery failed with status ${response.status}`);
      }
      const payload = await response.json();
      const scopesSupported = Array.isArray(payload.scopes_supported) ? payload.scopes_supported.join(' ') : null;
      if (scopesSupported) {
        setOidcScopesInput(scopesSupported);
      }
      setOidcDiscoveryMessage('Discovery succeeded. Review the generated scopes before saving.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'OIDC discovery failed.';
      setOidcDiscoveryMessage(message);
    } finally {
      setOidcDiscovering(false);
    }
  };

  const handleMetadataFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const text = await readFileAsText(file);
      const metadata = await parseSamlMetadata(text);
      setSamlMetadataPreview(metadata);
      setSamlMetadataFileName(file.name);
      if (metadata.entityId) {
        handleSamlConfigChange('idpEntityId', metadata.entityId);
      }
      if (metadata.ssoUrl) {
        handleSamlConfigChange('idpSsoUrl', metadata.ssoUrl);
      }
      if (metadata.certificates.length > 0) {
        const primaryCert = metadata.certificates[0].pem;
        handleSamlConfigChange('idpCertificate', primaryCert);
        setSamlCertificateInput(primaryCert);
      }
      setFormError(null);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to parse metadata file.';
      setFormError(message);
    }
  };

  const handleFetchMetadataUrl = async () => {
    if (samlMetadataUrl.trim().length === 0) {
      setFormError('Enter a metadata URL to fetch.');
      return;
    }
    try {
      setFormError(null);
      const response = await fetch(samlMetadataUrl, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`Metadata fetch failed with status ${response.status}`);
      }
      const text = await response.text();
      const metadata = await parseSamlMetadata(text);
      setSamlMetadataPreview(metadata);
      if (metadata.entityId) {
        handleSamlConfigChange('idpEntityId', metadata.entityId);
      }
      if (metadata.ssoUrl) {
        handleSamlConfigChange('idpSsoUrl', metadata.ssoUrl);
      }
      if (metadata.certificates.length > 0) {
        const primaryCert = metadata.certificates[0].pem;
        handleSamlConfigChange('idpCertificate', primaryCert);
        setSamlCertificateInput(primaryCert);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch metadata URL.';
      setFormError(message);
    }
  };

  const trimOrUndefined = (value: string | null | undefined): string | undefined => {
    const trimmed = typeof value === 'string' ? value.trim() : null;
    return trimmed ? trimmed : undefined;
  };

  const toOptionalNumber = (value: number | null | undefined): number | undefined => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return undefined;
    }
    return value;
  };

  const buildPayload = (clientSecretIdOverride?: string): IdpConnectionCreate => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    if (connectionType === 'oidc') {
      const resolvedClientSecretId = (clientSecretIdOverride ?? oidcConfig.clientSecretId)?.trim();
      if (!resolvedClientSecretId) {
        throw new Error('clientSecretId is required for OIDC connections.');
      }
      const oidcPayload: IdpConnectionOidcCreate = {
        type: 'oidc',
        name: trimmedName,
        config: {
          issuer: oidcConfig.issuer.trim(),
          clientId: oidcConfig.clientId.trim(),
          clientSecretId: resolvedClientSecretId,
          tokenAuthMethod: oidcConfig.tokenAuthMethod ?? 'client_secret_basic',
          scopes: scopes.length > 0 ? scopes : undefined,
          enforcePkce: oidcConfig.enforcePkce ?? false,
          groupsClaimPath: trimOrUndefined(oidcConfig.groupsClaimPath),
          rolesClaimPath: trimOrUndefined(oidcConfig.rolesClaimPath),
          clockSkewSeconds: toOptionalNumber(oidcConfig.clockSkewSeconds)
        }
      };

      if (status) {
        oidcPayload.status = status;
      }
      if (trimmedDescription) {
        oidcPayload.description = trimmedDescription;
      }

      return oidcPayload;
    }

    let samlConfigPayload: IdpConnectionSamlCreateConfig = {
      nameIdFormat: samlConfig.nameIdFormat,
      groupsAttributeName: trimOrUndefined(samlConfig.groupsAttributeName),
      rolesAttributeName: trimOrUndefined(samlConfig.rolesAttributeName),
      allowIdpInitiated: samlConfig.allowIdpInitiated ?? true,
      clockSkewSeconds: toOptionalNumber(samlConfig.clockSkewSeconds)
    };

    if (samlInputMode === 'metadataUrl') {
      samlConfigPayload = {
        ...samlConfigPayload,
        idpMetadataUrl: samlMetadataUrl.trim() || undefined,
        idpEntityId: undefined,
        idpSsoUrl: undefined,
        idpCertificate: undefined
      };
    } else {
      samlConfigPayload = {
        ...samlConfigPayload,
        idpEntityId: trimOrUndefined(samlConfig.idpEntityId),
        idpSsoUrl: trimOrUndefined(samlConfig.idpSsoUrl),
        idpCertificate: trimOrUndefined(samlCertificateInput || samlConfig.idpCertificate)
      };
    }

    const samlPayload: IdpConnectionSamlCreate = {
      type: 'saml',
      name: trimmedName,
      config: samlConfigPayload
    };

    if (status) {
      samlPayload.status = status;
    }
    if (trimmedDescription) {
      samlPayload.description = trimmedDescription;
    }

    return samlPayload;
  };

  const handleSubmit = async () => {
    if (!validateBasics()) {
      return;
    }
    if (connectionType === 'oidc') {
      if (!validateOidc()) {
        return;
      }
    } else if (!validateSaml()) {
      return;
    }
    let secretIdForCleanup: string | null = null;
    try {
      setSubmitting(true);
      setFormError(null);
      let payload: IdpConnectionCreate;

      if (connectionType === 'oidc') {
        const secretPlaintext = clientSecretValue;
        const trimmedSecret = secretPlaintext.trim();
        if (trimmedSecret.length === 0) {
          setFormError('Client secret is required.');
          return;
        }
        const trimmedName = name.trim();
        try {
          const secret = await secretsApi.orgsOrgIdSecretsPost({
            orgId,
            secretCreate: {
              type: 'password',
              value: secretPlaintext,
              metadata: trimmedName ? { label: `${trimmedName} client secret` } : undefined
            }
          });
          if (!secret.id) {
            throw new Error('Secret creation response missing identifier.');
          }
          secretIdForCleanup = secret.id;
          payload = buildPayload(secret.id);
        } catch (caught: unknown) {
          if (caught instanceof ResponseError) {
            const text = await caught.response.text();
            setFormError(`Failed to store client secret (HTTP ${caught.response.status}). ${text || ''}`.trim());
          } else if (caught instanceof Error) {
            setFormError(caught.message);
          } else {
            setFormError('Failed to store client secret.');
          }
          return;
        }
      } else {
        payload = buildPayload();
      }

      const response = await idpConnectionsApi.orgsOrgIdIdpConnectionsPostRaw({ orgId, idpConnectionCreate: payload });
      const connection = await response.value();
      const etag = response.raw.headers.get('ETag');
      setClientSecretValue('');
      setCreatedResult({ connection, etag, openDrawer: false });
      setShowSuccess(true);
    } catch (error: unknown) {
      if (secretIdForCleanup) {
        try {
          await deleteSecretWithAuth({ secretsApi, orgId, secretId: secretIdForCleanup });
        } catch (cleanupError) {
          console.warn('Failed to clean up client secret after connection create error', cleanupError);
        }
      }
      if (error instanceof ResponseError) {
        const text = await error.response.text();
        setFormError(`Failed to create connection (HTTP ${error.response.status}). ${text || ''}`.trim());
      } else if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('Failed to create connection.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeWizard = () => {
    resetState(preferredType);
    onClose();
  };

  const finishSuccess = (openConnection: boolean) => {
    if (createdResult) {
      onCreated({
        ...createdResult,
        openDrawer: openConnection
      });
    }
    closeWizard();
  };

  const renderBasicsForm = () => (
    <div className="form-grid">
      <div className="form-field">
        <span className="form-field__label">Connection Type</span>
        <div className="chip-row">
          <button
            type="button"
            className={`chip-button ${connectionType === 'oidc' ? 'chip-button--active' : ''}`}
            onClick={() => setConnectionType('oidc')}
          >
            OIDC
          </button>
          <button
            type="button"
            className={`chip-button ${connectionType === 'saml' ? 'chip-button--active' : ''}`}
            onClick={() => setConnectionType('saml')}
          >
            SAML
          </button>
        </div>
      </div>

      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Name</span>
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} placeholder="Okta Production" />
        </label>

        <label className="form-field">
          <span className="form-field__label">Status</span>
          <select className="select" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="form-field">
        <span className="form-field__label">Description</span>
        <textarea
          className="textarea"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Optional note for administrators."
        />
      </label>
    </div>
  );

  const renderOidcForm = () => (
    <div className="form-grid">
      <div className="section-card">
        <div className="section-card__header">
          <h3 className="section-card__title">OIDC Settings</h3>
          <button type="button" className="ui-button" onClick={handleOidcDiscover} disabled={oidcDiscovering}>
            {oidcDiscovering ? 'Discovering…' : 'Discover'}
          </button>
        </div>
        <div className="form-grid form-grid--two">
          <label className="form-field">
            <span className="form-field__label">Issuer URL</span>
            <input
              className="input"
              value={oidcConfig.issuer}
              onChange={(event) => handleOidcConfigChange('issuer', event.target.value)}
              placeholder="https://idp.example.com/oauth2/default"
            />
            <span className="form-field__hint">We will use /.well-known/openid-configuration for discovery.</span>
          </label>

          <label className="form-field">
            <span className="form-field__label">Client ID</span>
            <input
              className="input"
              value={oidcConfig.clientId}
              onChange={(event) => handleOidcConfigChange('clientId', event.target.value)}
              placeholder="0oa12345abcd"
            />
          </label>

          <label className="form-field">
            <span className="form-field__label">Client Secret</span>
            <input
              className="input"
              type="password"
              value={clientSecretValue}
              onChange={(event) => setClientSecretValue(event.target.value)}
              placeholder="Paste client secret from IdP"
            />
            <span className="form-field__hint">
              Stored as a new secret automatically; the raw value cannot be viewed after save.
            </span>
          </label>

          <label className="form-field">
            <span className="form-field__label">Token Auth Method</span>
            <select
              className="select"
              value={oidcConfig.tokenAuthMethod ?? 'client_secret_basic'}
              onChange={(event) => handleOidcConfigChange('tokenAuthMethod', event.target.value as TokenAuthMethod)}
            >
              {tokenAuthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="form-field">
            <span className="form-field__label">Scopes</span>
            <input
              className="input"
              value={oidcScopesInput}
              onChange={(event) => setOidcScopesInput(event.target.value)}
              placeholder="openid email profile groups"
            />
            <span className="form-field__hint">Separate scopes with spaces.</span>
          </label>

          <label className="form-field checkbox-row">
            <input
              type="checkbox"
              className="checkbox"
              checked={oidcConfig.enforcePkce ?? false}
              onChange={(event) => handleOidcConfigChange('enforcePkce', event.target.checked)}
            />
            <span>Require PKCE</span>
          </label>
        </div>
      </div>

      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Groups Claim Path</span>
          <input
            className="input"
            value={oidcConfig.groupsClaimPath ?? ''}
            onChange={(event) => handleOidcConfigChange('groupsClaimPath', event.target.value)}
            placeholder="groups"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Roles Claim Path</span>
          <input
            className="input"
            value={oidcConfig.rolesClaimPath ?? ''}
            onChange={(event) => handleOidcConfigChange('rolesClaimPath', event.target.value)}
            placeholder="custom.roles"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Clock Skew (seconds)</span>
          <input
            className="input"
            type="number"
            value={oidcConfig.clockSkewSeconds ?? 120}
            onChange={(event) => handleOidcConfigChange('clockSkewSeconds', Number(event.target.value))}
          />
        </label>
      </div>

      {oidcDiscoveryMessage && <p className="form-field__hint">{oidcDiscoveryMessage}</p>}
    </div>
  );

  const renderSamlForm = () => (
    <div className="form-grid">
      <div className="section-card">
        <h3 className="section-card__title">Metadata Source</h3>
        <div className="chip-row">
          <button
            type="button"
            className={`chip-button ${samlInputMode === 'metadataFile' ? 'chip-button--active' : ''}`}
            onClick={() => setSamlInputMode('metadataFile')}
          >
            Upload metadata file
          </button>
          <button
            type="button"
            className={`chip-button ${samlInputMode === 'metadataUrl' ? 'chip-button--active' : ''}`}
            onClick={() => setSamlInputMode('metadataUrl')}
          >
            Metadata URL
          </button>
          <button
            type="button"
            className={`chip-button ${samlInputMode === 'manual' ? 'chip-button--active' : ''}`}
            onClick={() => setSamlInputMode('manual')}
          >
            Manual entry
          </button>
        </div>

        {samlInputMode === 'metadataFile' && (
          <div className="form-field">
            <span className="form-field__label">Upload XML</span>
            <input type="file" accept=".xml,.XML" onChange={handleMetadataFile} />
            {samlMetadataFileName && <span className="form-field__hint">Parsed {samlMetadataFileName}</span>}
          </div>
        )}

        {samlInputMode === 'metadataUrl' && (
          <div className="form-field">
            <span className="form-field__label">Metadata URL</span>
            <div className="copy-field">
              <input
                className="input"
                value={samlMetadataUrl}
                onChange={(event) => setSamlMetadataUrl(event.target.value)}
                placeholder="https://idp.example.com/metadata.xml"
              />
              <button type="button" className="copy-field__button" onClick={handleFetchMetadataUrl}>
                Fetch
              </button>
            </div>
          </div>
        )}

        {(samlInputMode === 'manual' || samlInputMode === 'metadataFile' || samlInputMode === 'metadataUrl') && (
          <div className="form-grid form-grid--two">
            <label className="form-field">
              <span className="form-field__label">IdP Entity ID</span>
              <input
                className="input"
                value={samlConfig.idpEntityId ?? ''}
                onChange={(event) => handleSamlConfigChange('idpEntityId', event.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">IdP SSO URL</span>
              <input
                className="input"
                value={samlConfig.idpSsoUrl ?? ''}
                onChange={(event) => handleSamlConfigChange('idpSsoUrl', event.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      {(samlInputMode === 'manual' || samlInputMode === 'metadataFile' || samlInputMode === 'metadataUrl') && (
        <div className="section-card">
          <div className="section-card__header">
            <h3 className="section-card__title">Certificates</h3>
          </div>
          <textarea
            className="textarea"
            value={samlCertificateInput}
            onChange={(event) => {
              setSamlCertificateInput(event.target.value);
              handleSamlConfigChange('idpCertificate', event.target.value);
            }}
            placeholder="Paste PEM formatted signing certificate"
          />
          {samlMetadataPreview && samlMetadataPreview.certificates.length > 0 && (
            <div className="form-field__hint">
              Parsed certificate fingerprints:{' '}
              {samlMetadataPreview.certificates
                .map((cert) => cert.fingerprint ?? 'N/A')
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>
      )}

      <div className="section-card">
        <h3 className="section-card__title">Advanced Options</h3>
        <div className="form-grid form-grid--two">
          <label className="form-field">
            <span className="form-field__label">NameID Format</span>
            <select
              className="select"
              value={samlConfig.nameIdFormat ?? 'unspecified'}
              onChange={(event) =>
                handleSamlConfigChange('nameIdFormat', event.target.value as IdpConnectionSamlCreateConfig['nameIdFormat'])
              }
            >
              {nameIdOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Groups Attribute</span>
            <input
              className="input"
              value={samlConfig.groupsAttributeName ?? ''}
              onChange={(event) => handleSamlConfigChange('groupsAttributeName', event.target.value)}
              placeholder="memberOf"
            />
          </label>
          <label className="form-field">
            <span className="form-field__label">Roles Attribute</span>
            <input
              className="input"
              value={samlConfig.rolesAttributeName ?? ''}
              onChange={(event) => handleSamlConfigChange('rolesAttributeName', event.target.value)}
              placeholder="role"
            />
          </label>
          <label className="form-field">
            <span className="form-field__label">Clock Skew (seconds)</span>
            <input
              className="input"
              type="number"
              value={samlConfig.clockSkewSeconds ?? 120}
              onChange={(event) => handleSamlConfigChange('clockSkewSeconds', Number(event.target.value))}
            />
          </label>
        </div>
        <label className="form-field checkbox-row">
          <input
            type="checkbox"
            className="checkbox"
            checked={samlConfig.allowIdpInitiated ?? true}
            onChange={(event) => handleSamlConfigChange('allowIdpInitiated', event.target.checked)}
          />
          <span>Allow IdP-initiated sign-ins</span>
        </label>
      </div>
    </div>
  );

  const renderSuccessContent = () => {
    if (!createdResult) {
      return null;
    }
    const { connection } = createdResult;
    const computedItems: Array<{ label: string; value: string }> = (() => {
      if (isOidcConnection(connection)) {
        const uris = connection.config.redirectUris ?? [];
        if (uris.length === 0) {
          return [];
        }
        return uris.map((uri, index) => ({
          label: uris.length > 1 ? `Redirect URI ${index + 1}` : 'Redirect URI',
          value: uri
        }));
      }

      if (isSamlConnection(connection)) {
        const items: Array<{ label: string; value: string }> = [];
        if (connection.config.acsUrl) {
          items.push({ label: 'ACS URL', value: connection.config.acsUrl });
        }
        if (connection.config.spEntityId) {
          items.push({ label: 'SP Entity ID', value: connection.config.spEntityId });
        }
        return items;
      }

      return [];
    })();

    return (
      <div className="form-grid">
        <div className="section-card">
          <h3 className="section-card__title">Connection created</h3>
          <p className="form-field__hint">Share the following values with your IdP administrator.</p>
          {computedItems.length > 0 ? (
            computedItems.map(({ label, value }) => (
              <div key={`${label}-${value}`} className="form-field">
                <span className="form-field__label">{label}</span>
                <CopyField value={value} />
              </div>
            ))
          ) : isOidcConnection(connection) ? (
            <p>No redirect URIs were returned.</p>
          ) : (
            <p>No ACS/SP identifiers returned.</p>
          )}
          <div className="mapping-rule-card__actions">
            <button type="button" className="ui-button" onClick={() => finishSuccess(false)}>
              Close
            </button>
            <button type="button" className="ui-button ui-button--primary" onClick={() => finishSuccess(true)}>
              Open connection
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className="form-grid">
      {renderBasicsForm()}
      {connectionType === 'oidc' ? renderOidcForm() : renderSamlForm()}
    </div>
  );

  const renderDrawerActions = () => {
    if (showSuccess) {
      return null;
    }
    return (
      <div className="drawer-icon-actions">
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Save connection"
          title="Save connection"
          onClick={handleSubmit}
          disabled={submitting}
        >
          <SaveIcon size={18} />
        </button>
      </div>
    );
  };

  if (!open) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={closeWizard}
      title={showSuccess ? 'Connection created' : 'Add IdP Connection'}
      subtitle={showSuccess ? undefined : connectionType === 'oidc' ? 'Configure an OIDC IdP connection' : 'Configure a SAML IdP connection'}
      width="wide"
      actions={renderDrawerActions()}
    >
      {!showSuccess && formError && (
        <p className="org-shell__banner" role="alert">
          {formError}
        </p>
      )}
      {showSuccess ? renderSuccessContent() : renderForm()}
    </Drawer>
  );
};

export default ConnectionWizard;
